import React, { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate, Link } from "react-router-dom";
import { ArrowRight, ChevronDown, CheckCircle2, ShieldCheck, MapPin, Sparkles } from "lucide-react";
import PublicLayout, { PageHero } from "../components/PublicLayout";
import { useLang } from "../context/LanguageContext";
import { findCategoryBySlug, findCity, cities, buildCopy, groupForSlug } from "../i18n/seoContent";
import { useCmsBlocks } from "../lib/useCmsBlocks";
import { setPageMeta, setRobots, buildFaqJsonLd, buildProfessionalServiceJsonLd, buildBreadcrumbJsonLd } from "../lib/seo";

const FaqItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <button onClick={() => setOpen(!open)} className="w-full text-left rounded-2xl border border-[#2A2A2A] bg-[#181818] p-5 hover:border-[#3a3a3d] transition-colors">
      <div className="flex items-center justify-between gap-4"><span className="text-white font-medium text-[16px]">{q}</span><ChevronDown size={20} className={`text-neutral-500 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} /></div>
      {open && <p className="text-[15px] text-neutral-400 leading-relaxed mt-3">{a}</p>}
    </button>
  );
};

const CategoryLanding = () => {
  const { category, city } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { lang, setLang } = useLang();
  const isSv = location.pathname.startsWith("/sv");

  const cat = findCategoryBySlug(category);
  const cityObj = city ? findCity(city) : null;
  const group = cat ? groupForSlug(cat.slug) : null;
  const blocks = useCmsBlocks();

  // Admin-approved SEO content overrides (AI Insights approval gate) stored as CMS blocks.
  const withOverrides = (copy, langKey) => {
    if (!cat) return copy;
    const base = `seo.${cat.slug}.${cityObj ? cityObj.slug : "all"}.${langKey}`;
    return {
      ...copy,
      metaTitle: blocks[`${base}.metaTitle`] || copy.metaTitle,
      metaDesc: blocks[`${base}.metaDesc`] || copy.metaDesc,
      intro: blocks[`${base}.intro`] || copy.intro,
    };
  };

  // Keep the global language in sync with the route locale
  useEffect(() => { setLang(isSv ? "sv" : "en"); }, [isSv, setLang]);

  useEffect(() => {
    // Soft-error (unknown category or invalid city) pages must not be indexed.
    if (!cat || (city && !cityObj)) { setRobots("noindex, nofollow"); return; }
    const langKey = isSv ? "sv" : "en";
    const copy = withOverrides(buildCopy(cat, cityObj, langKey), langKey);
    const enPath = `/book/${cat.slug}${cityObj ? `/${cityObj.slug}` : ""}`;
    const svPath = `/sv/boka/${cat.slug}${cityObj ? `/${cityObj.slug}` : ""}`;
    const mandatory = isSv
      ? [
          { q: "Ingår ljud- och ljusutrustning när jag bokar?", a: "Talanger anger själva på sina profiler om de tillhandahåller egen utrustning eller ej. Du kan enkelt filtrera och granska detta på artistens publika profil innan du väljer vem du vill samarbeta med." },
          { q: "Hur fungerar betalningen för bokningen?", a: "Talentsio hanterar inga betalningar mellan klient och talang. Betalningsuppgörelsen sker helt direkt mellan er på era villkor, vilket innebär att ni slipper dyra förmedlingsavgifter och dolda påslag." },
        ]
      : [
          { q: "Is sound and lighting equipment included when I book?", a: "Talents specify on their own profiles whether they provide their own equipment or not. You can easily filter and review this on the artist's public profile before choosing who to work with." },
          { q: "How does payment for the booking work?", a: "Talentsio does not handle any payments between client and talent. The payment agreement is made directly between you on your own terms, which means you avoid expensive intermediary fees and hidden markups." },
        ];
    const crumbs = [
      { name: isSv ? "Hem" : "Home", path: isSv ? "/sv" : "/" },
      { name: cat[langKey].name, path: isSv ? `/sv/boka/${cat.slug}` : `/book/${cat.slug}` },
    ];
    if (cityObj) crumbs.push({ name: cityObj.name, path: location.pathname });
    const jsonLd = [
      buildProfessionalServiceJsonLd({ categoryName: cat[langKey].name, cityName: cityObj ? cityObj.name : null, description: copy.metaDesc, ratingAvg: 0, ratingCount: 0 }),
      buildFaqJsonLd([...mandatory, ...copy.faq]),
      buildBreadcrumbJsonLd(crumbs),
    ];
    setPageMeta({
      title: copy.metaTitle, description: copy.metaDesc, lang: langKey,
      path: location.pathname, enPath, svPath, robots: "index, follow", jsonLd,
      image: `/og/${cat.slug}.jpg`,
    });
  }, [cat, cityObj, isSv, location.pathname, blocks]);

  // Graceful SEO fallback: unknown/deleted category OR an invalid city combination.
  // Never show a broken empty page - render an engaging shell that funnels to booking step 1.
  if (!cat || (city && !cityObj)) {
    const bookHref = "/BookingRequest";
    return (
      <PublicLayout>
        <div className="max-w-[640px] mx-auto px-6 pt-[150px] pb-28 text-center" data-testid="landing-fallback">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[#FF5C00]/12 border border-[#FF5C00]/30 mb-7">
            <Sparkles size={26} className="text-[#FF7A2F]" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white">
            {isSv ? "Vi hittar rätt talang för ditt event" : "Let's find the right talent for your event"}
          </h1>
          <p className="text-neutral-400 text-base mt-4 max-w-[500px] mx-auto">
            {isSv
              ? "Den här sidan finns inte längre, men du är bara ett steg från att skicka en gratis förfrågan och få prissatta bud från verifierade talanger."
              : "This page is no longer available, but you're one step away from posting a free request and getting priced offers from verified talent."}
          </p>
          <Link to={bookHref} data-testid="fallback-cta"
            className="mt-8 inline-flex items-center gap-2 bg-[#1A8A4A] hover:bg-[#20A458] text-white font-semibold px-7 py-3.5 rounded-xl transition-colors">
            {isSv ? "Skicka en gratis förfrågan" : "Post a free request"} <ArrowRight size={18} />
          </Link>
        </div>
      </PublicLayout>
    );
  }

  const useLangKey = isSv ? "sv" : "en";
  const copy = withOverrides(buildCopy(cat, cityObj, useLangKey), useLangKey);
  const bokaBase = isSv ? "/sv/boka" : "/book";
  const catName = cat[useLangKey].name;
  const cityName = cityObj ? cityObj.name : null;
  const benefits = isSv
    ? ["Gratis att skicka en förfrågan", "Endast verifierade talanger", "Fast slutpris inkl. allt", "Direkt godkännande"]
    : ["Free to post a request", "Only verified talent", "Fixed final price incl. everything", "Instant one-tap approval"];

  // 3-column trust anchor (platform-accurate, du-form on SV).
  const trust = isSv
    ? [
        { t: "Fast slutpris", d: "Kund betalar fast slutpris inkl. allt, inga dolda avgifter." },
        { t: "Utrustning efter behov", d: "Se direkt på talangernas profiler vem som erbjuder komplett ljud och ljus." },
        { t: "Direktkontakt", d: "Prata direkt med verifierade artister i Sverige för din kick-off, ditt bröllop eller din fest." },
      ]
    : [
        { t: "Fixed final price", d: "Customers pay a fixed final price including everything, no hidden fees." },
        { t: "Equipment as needed", d: "See directly on the talent profiles who offers complete sound and lighting." },
        { t: "Direct contact", d: "Talk directly with verified artists in Sweden for your kick-off, wedding or party." },
      ];

  // Mandatory platform-boundary FAQs, prepended to the category FAQ set.
  const mandatoryFaq = isSv
    ? [
        { q: "Ingår ljud- och ljusutrustning när jag bokar?", a: "Talanger anger själva på sina profiler om de tillhandahåller egen utrustning eller ej. Du kan enkelt filtrera och granska detta på artistens publika profil innan du väljer vem du vill samarbeta med." },
        { q: "Hur fungerar betalningen för bokningen?", a: "Talentsio hanterar inga betalningar mellan klient och talang. Betalningsuppgörelsen sker helt direkt mellan er på era villkor, vilket innebär att ni slipper dyra förmedlingsavgifter och dolda påslag." },
      ]
    : [
        { q: "Is sound and lighting equipment included when I book?", a: "Talents specify on their own profiles whether they provide their own equipment or not. You can easily filter and review this on the artist's public profile before choosing who to work with." },
        { q: "How does payment for the booking work?", a: "Talentsio does not handle any payments between client and talent. The payment agreement is made directly between you on your own terms, which means you avoid expensive intermediary fees and hidden markups." },
      ];
  const faqAll = [...mandatoryFaq, ...copy.faq];

  return (
    <PublicLayout>
      <PageHero eyebrow={catName.toUpperCase()} title={copy.h1} subtitle={copy.intro} />
      <section className="max-w-[820px] mx-auto px-6 -mt-6 pb-4 text-center">
        <Link to={`/BookingRequest?category_group=${group}`} className="inline-flex items-center gap-2 bg-[#1A8A4A] hover:bg-[#20A458] text-white font-semibold px-7 py-3.5 rounded-xl transition-colors" data-testid="landing-cta">{copy.cta} <ArrowRight size={18} /></Link>
      </section>

      {/* 3-column trust anchor */}
      <section className="max-w-[1080px] mx-auto px-6 py-8" data-testid="trust-banner">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {trust.map((c, i) => (
            <div key={i} className="rounded-2xl border border-[#2A2A2A] bg-[#181818] p-6">
              <div className="flex items-center gap-2 text-[#FF7A2F] font-bold text-[16px]"><ShieldCheck size={18} /> {c.t}</div>
              <p className="text-[14px] text-neutral-400 leading-relaxed mt-2">{c.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-[820px] mx-auto px-6 py-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {benefits.map((b) => (
            <div key={b} className="flex items-center gap-3 rounded-xl border border-[#2A2A2A] bg-[#181818] px-5 py-4">
              <CheckCircle2 size={18} className="text-[#2fbe6e] shrink-0" /><span className="text-[15px] text-neutral-200">{b}</span>
            </div>
          ))}
        </div>
      </section>

      {/* City cross-links for SEO */}
      <section className="max-w-[820px] mx-auto px-6 pb-12">
        <h2 className="text-white font-semibold mb-4">{copy.otherCitiesTitle}</h2>
        <div className="flex flex-wrap gap-2">
          {cities.filter((c) => c.slug !== city).map((c) => (
            <Link key={c.slug} to={`${bokaBase}/${cat.slug}/${c.slug}`} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-[#181818] border border-[#2A2A2A] text-[14px] text-neutral-300 hover:border-[#FF5C00] hover:text-white transition-colors">
              <MapPin size={13} /> {c.name}
            </Link>
          ))}
        </div>
      </section>

      <section className="max-w-[760px] mx-auto px-6 pb-24">
        <h2 className="text-white font-semibold mb-4">{isSv ? "Vanliga frågor" : "Frequently asked questions"}</h2>
        <div className="space-y-3">{faqAll.map((f) => <FaqItem key={f.q} {...f} />)}</div>
      </section>
    </PublicLayout>
  );
};

export default CategoryLanding;
